import { Component } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { AuthService, BreadcrumbService, PartnershipService, StateService } from 'src/app/services';
import { IdentityWebApiClient, AddPartnershipRequest, ListPartnershipsRequest, PartnershipStatus, Partnership } from 'identity-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CompanyType, ICompany, ListManufacturersRequest, ListManufacturersResponse, SituViewWebApiClient } from 'situview-webapi-client';
import { MatDialog } from '@angular/material/dialog';
import { PartnershipActionConfirmationDialogComponent } from '../partnership-action-confirmation-dialog/partnership-action-confirmation-dialog.component';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ViewControls } from 'src/app/shared/models/view-controls';

@Component({
  selector: 'partnership-request',
  templateUrl: './partnership-request.component.html',
  styleUrls: ['./partnership-request.component.scss']
})

export class PartnershipRequestComponent extends SituBaseComponent {
  viewControls = {
    pageIndex: 0,
    pageSize: 25,
    totalItems: 0,
    pageSizeOptions: [25, 50, 100],
    displayedColumns: ['company', 'cta']
  } as ViewControls;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  enabledOnly: boolean = true;
  previousSearchTerm = "";
  searchTerm = "";
  manufacturers: ICompany[] | undefined;
  partnershipsDataSource = new MatTableDataSource<ICompany>();
  selectedCompanyId: string | undefined;
  excludedCompanyIds: string[] = new Array<string>();

  constructor(
    authService: AuthService,
    stateService: StateService,
    private identityWebApiClient: IdentityWebApiClient,
    private situViewWebApiClient: SituViewWebApiClient,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private partnershipService: PartnershipService) {
      super(authService, stateService);
  }

  protected onSelectedCompanyChange(): void {
    this.loadPartnerships()
      .then(() => {
        this.loadManufacturers();
      });
  }

  companyTypes = CompanyType;
  getCompanyLogo(companyId: string) {
    return this.manufacturers!.find(x => x.id === companyId)?.logoUrl;
  }
  getCompanyName(companyId: string) {
    return this.manufacturers!.find(x => x.id === companyId)?.name;
  }

  loadManufacturers() {
    this.loading = true;

    let request = new ListManufacturersRequest();

    if (this.searchTermChanged()) this.resetSearch();
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    request.searchTerm = this.searchTerm;
    request.companyIdsToExclude = this.excludedCompanyIds.join(",");
    this.previousSearchTerm = this.searchTerm;

    return this.situViewWebApiClient.listManufacturers(request)
      .then((response: ListManufacturersResponse) => {
        this.manufacturers = response.manufacturers?.filter(x => {
          if (this.partnerships && this.partnerships.length > 0) {
            return !this.partnerships.some(p => p.requesteeCompanyId === x.id || p.requesterCompanyId === x.id);
          }
          return true;
        }) ?? [];

    this.viewControls.totalItems = response.totalItems ?? 0;
    this.loading = false;
  });
  }

  requestPartnership(manufacturer: ICompany) {
    this.selectedCompanyId = manufacturer.id;
    const dialogRef = this.dialog.open(PartnershipActionConfirmationDialogComponent, {
      width: '350px',
      data: { partnerName: manufacturer.name, request: true }
    });

    this._subscriptions.push(dialogRef.afterClosed().subscribe(result => {
      if (result) {
        let request = new AddPartnershipRequest();
        request.requesterCompanyId = this.selectedCompany?.id;
        request.requesteeCompanyId = manufacturer?.id;

        this.identityWebApiClient.addPartnership(request)
          .then(response => {
            if (response.success) {
              this.snackBar.open(`Partnership request sent to ${manufacturer.name}!`, 'OK', { duration: 2000 });
              if (manufacturer?.id){
                this.excludedCompanyIds.push(manufacturer?.id);
                this.loadManufacturers();
              }
            } else {
              this.snackBar.open(`Error: ${response.errorMessages}!`, 'OK', { duration: 2000 });
            }
            this.selectedCompanyId = undefined;
          })
          .catch(error => {
            this.selectedCompanyId = undefined;
            this.snackBar.open(`Error requesting Partnership!`, 'OK', { duration: 2000 });
          });
      } else {
        this.snackBar.open(`Partnership Request not Sent!`, 'OK', { duration: 2000 });
        this.selectedCompanyId = undefined;
      }
    }));
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.loadManufacturers();
  }

  resetSearch() {
    this.viewControls.pageIndex = 0;
    this.viewControls.pageSize = 25;
    this.viewControls.totalItems = 0;
  }

  private searchTermChanged(): boolean {
    return this.searchTerm.toLowerCase() !== this.previousSearchTerm.toLowerCase();
  }

  partnerships: Partnership[] | undefined;
  loadPartnerships() {
    this.loading = true;

    let request = new ListPartnershipsRequest();

    if (this.searchTermChanged()) this.resetSearch();
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    request.searchTerm = this.searchTerm;
    request.companyId = this.selectedCompany?.id;
    //request.partnershipStatuses = [PartnershipStatus.Approved, PartnershipStatus.Reconnected];
    this.previousSearchTerm = this.searchTerm;

    return this.partnershipService.ListPartnerships(request)
      .then(response => {
        this.partnerships = response.partnerships;
        //this.partnershipsDataSource.data = response.partnerships ?? [];
        //this.viewControls.totalItems = response.totalItems ?? 0;
      })
      .catch((err: any) => {})
      .finally(() => { this.loading = false; });
  }
}
